const { request, requestConfig, offlineSerialize, util } = require('./internal');
const { defaultEnv } = requestConfig;
const { ErrorType, ErrorUtil } = require("@oneledger/middle_utility").TierError;
const { requestErrors } = ErrorType;

/**
 * @description delegateTxOffline prepares delegate tx
 * @param parameters {Object} object of parameters
 * @param parameters.delegateAddress {string} delegator address
 * @param parameters.amount {Object} amount object
 * @param parameters.amount.currency {string} currency name
 * @param parameters.amount.value {string} amount
 * @param [parameters.gasAdjustment] {number} tx gas adjustment, non-negative int, if not provided, default value will be 0
 * @param [env] {Object} environment parameters object
 * @param env.url {string} environment url (default is Kainos)
 * @param env.storeConfig {Object} store config
 * @param env.storeConfig.platform {string} store type. "electron" or "browser" (default is electron)
 * @param env.storeConfig.storeLocation {string} store location path (default is __dirname)
 * @return {Promise<response|error>} Promise.reject: error object , Promise.resolve: response object has rawTx with tx fee estimation
 */
async function delegateTxOffline({ delegateAddress, amount, gasAdjustment = 0 }, env = defaultEnv) {
    if (!util.isValidOLTAddress(delegateAddress)) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidAddress));

    // amount convert
    const sendingCurrency = await util.getCurrency(amount.currency);

    const checkAmountValueResult = util.requestPriceConverter(amount.value, sendingCurrency.decimal);
    if (!checkAmountValueResult) return Promise.reject(ErrorUtil.errorWrap(requestErrors.IllegalAmount));
    amount.value = checkAmountValueResult;

    // check gasAdjustment if provided
    if (!Number.isInteger(gasAdjustment) || gasAdjustment < 0) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidGasAdjustment));

    const { response: gasPrice } = await request.queryGasPrice(env);

    const txPayload = {
        delegationAddress: delegateAddress,
        amount
    };

    const assembledTx = util.assembleTxData(requestConfig.TxTypes.ADD_NETWORK_DELEGATE, txPayload, gasPrice, gasAdjustment);
    const { gas } = assembledTx.fee;
    const { response: feeEstimation } = await util.txFeeEstimator(gas, gasPrice);
    const rawTx = util.rawTxStructure(offlineSerialize.jsonObjectToBase64(assembledTx));
    return Promise.resolve(ErrorUtil.responseWrap({ ...rawTx, ...{ feeEstimation } }))
}

/**
 * @description undelegateTxOffline prepares undelegate tx
 * @param parameters {Object} object of parameters
 * @param parameters.undelegateAddress {string} delegator address
 * @param parameters.amount {Object} amount object
 * @param parameters.amount.currency {string} currency name
 * @param parameters.amount.value {string} amount
 * @param [parameters.gasAdjustment] {number} tx gas adjustment, non-negative int, if not provided, default value will be 0
 * @param [env] {Object} environment parameters object
 * @param env.url {string} environment url (default is Kainos)
 * @param env.storeConfig {Object} store config
 * @param env.storeConfig.platform {string} store type. "electron" or "browser" (default is electron)
 * @param env.storeConfig.storeLocation {string} store location path (default is __dirname)
 * @return {Promise<response|error>} Promise.reject: error object , Promise.resolve: response object has rawTx with tx fee estimation
 */
async function undelegateTxOffline({ undelegateAddress, amount, gasAdjustment = 0 }, env = defaultEnv) {
    if (!util.isValidOLTAddress(undelegateAddress)) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidAddress));

    // amount convert
    const sendingCurrency = await util.getCurrency(amount.currency);

    const checkAmountValueResult = util.requestPriceConverter(amount.value, sendingCurrency.decimal);
    if (!checkAmountValueResult) return Promise.reject(ErrorUtil.errorWrap(requestErrors.IllegalAmount));
    amount.value = checkAmountValueResult;

    // check gasAdjustment if provided
    if (!Number.isInteger(gasAdjustment) || gasAdjustment < 0) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidGasAdjustment));

    const { response: gasPrice } = await request.queryGasPrice(env);

    const txPayload = {
        delegator: undelegateAddress,
        amount
    };

    const assembledTx = util.assembleTxData(requestConfig.TxTypes.NETWORK_UNDELEGATE, txPayload, gasPrice, gasAdjustment);
    const { gas } = assembledTx.fee;
    const { response: feeEstimation } = await util.txFeeEstimator(gas, gasPrice);
    const rawTx = util.rawTxStructure(offlineSerialize.jsonObjectToBase64(assembledTx));
    return Promise.resolve(ErrorUtil.responseWrap({ ...rawTx, ...{ feeEstimation } }))
}

/**
 * @description delegateRewardWithdrawalTxOffline prepares delegate rewards withdrawal tx
 * @param parameters {Object} object of parameters
 * @param parameters.delegateAddress {string} delegator address
 * @param parameters.amount {Object} amount object
 * @param parameters.amount.currency {string} currency name
 * @param parameters.amount.value {string} amount
 * @param [parameters.gasAdjustment] {number} tx gas adjustment, non-negative int, if not provided, default value will be 0
 * @param [env] {Object} environment parameters object
 * @param env.url {string} environment url (default is Kainos)
 * @param env.storeConfig {Object} store config
 * @param env.storeConfig.platform {string} store type. "electron" or "browser" (default is electron)
 * @param env.storeConfig.storeLocation {string} store location path (default is __dirname)
 * @return {Promise<response|error>} Promise.reject: error object , Promise.resolve: response object has rawTx with tx fee estimation
 */
async function delegateRewardWithdrawalTxOffline({ delegateAddress, amount, gasAdjustment = 0 }, env = defaultEnv) {
    if (!util.isValidOLTAddress(delegateAddress)) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidAddress));

    // amount convert
    const sendingCurrency = await util.getCurrency(amount.currency);

    const checkAmountValueResult = util.requestPriceConverter(amount.value, sendingCurrency.decimal);
    if (!checkAmountValueResult) return Promise.reject(ErrorUtil.errorWrap(requestErrors.IllegalAmount));
    amount.value = checkAmountValueResult;

    // check gasAdjustment if provided
    if (!Number.isInteger(gasAdjustment) || gasAdjustment < 0) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidGasAdjustment));

    const { response: gasPrice } = await request.queryGasPrice(env);

    const txPayload = {
        delegator: delegateAddress,
        amount
    };

    const assembledTx = util.assembleTxData(requestConfig.TxTypes.REWARDS_WITHDRAW_NETWORK_DELEGATE, txPayload, gasPrice, gasAdjustment);
    const { gas } = assembledTx.fee;
    const { response: feeEstimation } = await util.txFeeEstimator(gas, gasPrice);
    const rawTx = util.rawTxStructure(offlineSerialize.jsonObjectToBase64(assembledTx));
    return Promise.resolve(ErrorUtil.responseWrap({ ...rawTx, ...{ feeEstimation } }))
}

/**
 * @description delegateRewardReinvestTxOffline prepares delegate rewards reinvest tx
 * @param parameters {Object} object of parameters
 * @param parameters.delegateAddress {string} delegator address
 * @param parameters.amount {Object} amount object
 * @param parameters.amount.currency {string} currency name
 * @param parameters.amount.value {string} amount
 * @param [parameters.gasAdjustment] {number} tx gas adjustment, non-negative int, if not provided, default value will be 0
 * @param [env] {Object} environment parameters object
 * @param env.url {string} environment url (default is Kainos)
 * @param env.storeConfig {Object} store config
 * @param env.storeConfig.platform {string} store type. "electron" or "browser" (default is electron)
 * @param env.storeConfig.storeLocation {string} store location path (default is __dirname)
 * @return {Promise<response|error>} Promise.reject: error object , Promise.resolve: response object has rawTx with tx fee estimation
 */
async function delegateRewardReinvestTxOffline({ delegateAddress, amount, gasAdjustment = 0 }, env = defaultEnv) {
    if (!util.isValidOLTAddress(delegateAddress)) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidAddress));

    // amount convert
    const sendingCurrency = await util.getCurrency(amount.currency);

    const checkAmountValueResult = util.requestPriceConverter(amount.value, sendingCurrency.decimal);
    if (!checkAmountValueResult) return Promise.reject(ErrorUtil.errorWrap(requestErrors.IllegalAmount));
    amount.value = checkAmountValueResult;

    // check gasAdjustment if provided
    if (!Number.isInteger(gasAdjustment) || gasAdjustment < 0) return Promise.reject(ErrorUtil.errorWrap(requestErrors.InvalidGasAdjustment));

    const { response: gasPrice } = await request.queryGasPrice(env);

    const txPayload = {
        delegator: delegateAddress,
        amount
    };

    const assembledTx = util.assembleTxData(requestConfig.TxTypes.REWARDS_REINVEST_NETWORK_DELEGATE, txPayload, gasPrice, gasAdjustment);
    const { gas } = assembledTx.fee;
    const { response: feeEstimation } = await util.txFeeEstimator(gas, gasPrice);
    const rawTx = util.rawTxStructure(offlineSerialize.jsonObjectToBase64(assembledTx));
    return Promise.resolve(ErrorUtil.responseWrap({ ...rawTx, ...{ feeEstimation } }))
}

module.exports = {
    delegateTxOffline,
    undelegateTxOffline,
    delegateRewardWithdrawalTxOffline,
    delegateRewardReinvestTxOffline
};

